import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
    HostBinding, Input, Output, forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { DxcIconComponent } from '../icon/dxc-icon.component';

export type DxcRatingSize = 'sm' | 'md' | 'lg';

/**
 * Calificación en estrellas, de lectura y de entrada.
 *
 * Sustituye a `<app-starsrate>`, que pintaba glifos de Font Awesome sin teclado
 * ni rol y sin forma de enseñar un 4.8. Una sola pieza para los dos usos, con el
 * mismo dibujo, para que lo que se califica y lo que se lee después sean la
 * misma estrella.
 *
 * Decisiones:
 *
 * - **Lectura y entrada, un solo `readonly`.** De lectura es una imagen con su
 *   etiqueta («4.8 de 5») y admite decimales: la estrella parcial se recorta
 *   por ancho, no con un tono intermedio. De entrada es un `radiogroup` de
 *   estrellas, con el foco ambulante (solo una estrella está en el orden de
 *   tabulación) y las flechas que mueven y eligen a la vez, como cualquier radio.
 * - **El valor no depende del color.** Contorno es «sin valor» y relleno es «con
 *   valor»: dos formas. El relleno toma `--dxc-warning-solid`.
 * - **En entrada el valor es entero** y se redondea al mostrarlo; en lectura el
 *   decimal se respeta tal cual.
 * - **El objetivo crece con la talla** (`lg` mide 44px), no con la estrella: el
 *   dibujo y la zona de toque son dos medidas distintas.
 */
@Component({
    standalone: true,
    selector: 'dxc-rating',
    templateUrl: './dxc-rating.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DxcIconComponent],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DxcRatingComponent), multi: true }],
})
export class DxcRatingComponent implements ControlValueAccessor {

    /** 0–`max`. Los decimales solo se dibujan en lectura. */
    @Input() set value(v: number | null | undefined) {
        const n = Number(v);
        this._value = Number.isFinite(n) ? Math.min(this.max, Math.max(0, n)) : 0;
    }
    get value() { return this._value; }

    @Input() max = 5;
    @Input() readonly = false;
    @Input() size: DxcRatingSize = 'md';
    @Input() ariaLabel = '';
    /** Cuántas calificaciones hay detrás: se enseña como «(23)». */
    @Input() count?: number | null;
    /** Enseña la cifra («4.8») junto a las estrellas. */
    @Input() showValue = false;

    @Output() valueChange = new EventEmitter<number>();

    disabled = false;
    /** Estrella sobre la que está el puntero: vista previa, no valor. */
    preview: number | null = null;

    private _value = 0;
    #onChange: (v: number) => void = () => { };
    #onTouched: () => void = () => { };

    constructor(private cdr: ChangeDetectorRef, private host: ElementRef<HTMLElement>) { }

    @HostBinding('class.dxc-rating') readonly hostClass = true;
    @HostBinding('class.dxc-rating--sm') get isSm() { return this.size === 'sm'; }
    @HostBinding('class.dxc-rating--lg') get isLg() { return this.size === 'lg'; }
    @HostBinding('class.dxc-rating--readonly') get isReadonly() { return this.readonly; }

    get stars(): number[] { return Array.from({ length: Math.max(1, Math.floor(this.max)) }, (_, i) => i + 1); }

    /** El valor entero con el que se pinta y se marca la entrada. */
    get rounded() { return Math.round(this._value); }

    get valueText() { return this._value.toFixed(1); }

    get summary() {
        const label = this.ariaLabel || 'Calificación';
        return `${label}: ${this.valueText} de ${this.max}`;
    }

    /** La estrella que entra en el orden de tabulación: la elegida, o la primera. */
    get tabStop() { return this.rounded >= 1 ? this.rounded : 1; }

    /** Porcentaje de la estrella `n` que va llena. */
    fill(n: number): number {
        const shown = !this.readonly && this.preview !== null ? this.preview : (this.readonly ? this._value : this.rounded);
        return Math.round(Math.min(1, Math.max(0, shown - (n - 1))) * 100);
    }

    select(n: number) {
        if (this.disabled || this.readonly) return;
        this._value = n;
        this.preview = null;
        this.#onChange(n);
        this.valueChange.emit(n);
        this.cdr.markForCheck();
    }

    touch() { this.#onTouched(); }

    onKeydown(event: KeyboardEvent) {
        if (this.disabled || this.readonly) return;
        const max = this.stars.length;
        const current = this.tabStop;
        let next: number | null = null;
        switch (event.key) {
            case 'ArrowRight': case 'ArrowUp': next = Math.min(max, current + 1); break;
            case 'ArrowLeft': case 'ArrowDown': next = Math.max(1, current - 1); break;
            case 'Home': next = 1; break;
            case 'End': next = max; break;
            default: return;
        }
        event.preventDefault();
        this.select(next);
        // El foco acompaña a la selección, como en un radio nativo.
        setTimeout(() => {
            const buttons = this.host.nativeElement.querySelectorAll<HTMLButtonElement>('button[role="radio"]');
            buttons[next! - 1]?.focus();
        });
    }

    // --- ControlValueAccessor ------------------------------------------------

    writeValue(value: number | null) { this.value = value; this.cdr.markForCheck(); }
    registerOnChange(fn: (v: number) => void) { this.#onChange = fn; }
    registerOnTouched(fn: () => void) { this.#onTouched = fn; }
    setDisabledState(disabled: boolean) { this.disabled = disabled; this.cdr.markForCheck(); }
}
